import React from 'react';
import { DESKTOP_APP_DOWNLOAD_URL } from '../constants/appMeta';
import './DesktopAppGuide.css';

/**
 * DesktopAppGuide — the single source of truth for "how do I pair a device?"
 *
 * Both the first-run flow (DeviceSelectionPrompt) and the settings flow
 * (SessionPanel) used to spell out the pairing steps in their own words, which
 * drifted and — crucially — never told a brand-new operator where to *get* the
 * desktop app. This renders the same three-step story everywhere:
 *
 *   1. Install & launch the desktop app on the computer you want to watch.
 *   2. Generate a pairing code (done right here).
 *   3. Type the code into the desktop app.
 *
 * `activeStep` (1-3) highlights the step the surrounding UI is currently on so
 * the guide doubles as a progress indicator. Pass `showDownload={false}` on the
 * settings surface where the operator has obviously already installed the app.
 */
const STEPS = [
  {
    n: 1,
    title: 'Install the desktop app',
    body: 'Download and launch it on the computer you want to watch. It connects on its own.',
  },
  {
    n: 2,
    title: 'Generate a pairing code',
    body: 'Pick an access level and generate a code below. It stays valid for a few minutes.',
  },
  {
    n: 3,
    title: 'Enter the code on the desktop',
    body: 'Type the code into the desktop app and it links to your account instantly.',
  },
];

const DesktopAppGuide = ({ activeStep = 0, showDownload = true, className = '' }) => {
  const hasDownload = showDownload && !!DESKTOP_APP_DOWNLOAD_URL;

  return (
    <div className={`desktop-app-guide ${className}`.trim()}>
      <ol className="dag-steps">
        {STEPS.map((s) => (
          <li
            key={s.n}
            className={`dag-step${activeStep === s.n ? ' active' : ''}${activeStep > s.n ? ' done' : ''}`}
          >
            <span className="dag-step-num" aria-hidden="true">
              {activeStep > s.n ? '✓' : s.n}
            </span>
            <span className="dag-step-text">
              <span className="dag-step-title">{s.title}</span>
              <span className="dag-step-body">{s.body}</span>
            </span>
          </li>
        ))}
      </ol>

      {hasDownload && (
        <a
          className="dag-download"
          href={DESKTOP_APP_DOWNLOAD_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M10 2a1 1 0 011 1v7.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 10.586V3a1 1 0 011-1z" />
            <path d="M4 15a1 1 0 011 1v1h10v-1a1 1 0 112 0v1a2 2 0 01-2 2H5a2 2 0 01-2-2v-1a1 1 0 011-1z" />
          </svg>
          Download the desktop app
        </a>
      )}
    </div>
  );
};

export default DesktopAppGuide;
